import { useAppSelector } from "@vv/state";
import { ROUTES } from "@vv/types";
import { AnalyticsPage } from "@vv/analytics-frontend";
import { AuditLogListPage } from "@vv/audit-log-frontend";
import { DashboardPage } from "@vv/dashboard-frontend";
import { KanbanPage } from "@vv/kanban-frontend";
import { RequestDetailPage, RequestsListPage, RequestWizardPage } from "@vv/requests-frontend";
import { TeamDetailPage, TeamsListPage } from "@vv/teams-frontend";
import { UsersListPage } from "@vv/users-frontend";
import { Route, Routes } from "react-router-dom";
import { AppLayout } from "../components/AppLayout";
import { ForgotPasswordPage } from "../pages/auth/ForgotPasswordPage";
import { LoginPage } from "../pages/auth/LoginPage";
import { ResetPasswordPage } from "../pages/auth/ResetPasswordPage";
import { SsoCallbackPage } from "../pages/auth/SsoCallbackPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { ProtectedRoute } from "./ProtectedRoute";
import { PublicOnlyRoute } from "./PublicOnlyRoute";

/**
 * Feature modules (e.g. `@vv/teams-frontend`, `@vv/requests-frontend`) never
 * read the app-wide auth state directly - that would be a circular
 * workspace dependency, since `@vv/state` composes the store by
 * importing their reducers. Instead the composition root (here) reads
 * `isOwner`/`id` once and passes them down as props.
 */
function DashboardRoute() {
  const user = useAppSelector((state) => state.auth.user);
  return (
    <DashboardPage
      currentUserId={user?.id ?? ""}
      isOwner={user?.isOwner ?? false}
      isViewer={user?.isViewer ?? false}
    />
  );
}

function TeamsListRoute() {
  const isOwner = useAppSelector((state) => state.auth.user?.isOwner ?? false);
  return <TeamsListPage isOwner={isOwner} />;
}

function TeamDetailRoute() {
  const isOwner = useAppSelector((state) => state.auth.user?.isOwner ?? false);
  return <TeamDetailPage isOwner={isOwner} />;
}

function KanbanRoute() {
  const user = useAppSelector((state) => state.auth.user);
  return <KanbanPage currentUserId={user?.id ?? ""} isOwner={user?.isOwner ?? false} />;
}

function RequestDetailRoute() {
  const user = useAppSelector((state) => state.auth.user);
  return <RequestDetailPage currentUserId={user?.id ?? ""} isOwner={user?.isOwner ?? false} />;
}

function UsersListRoute() {
  const user = useAppSelector((state) => state.auth.user);
  return <UsersListPage currentUserId={user?.id ?? ""} isOwner={user?.isOwner ?? false} />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route
        path={ROUTES.login}
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path={ROUTES.forgotPassword}
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path={ROUTES.resetPassword}
        element={
          <PublicOnlyRoute>
            <ResetPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route path={ROUTES.ssoCallback} element={<SsoCallbackPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardRoute />} />
        <Route path={ROUTES.teams} element={<TeamsListRoute />} />
        <Route path={ROUTES.teamDetailPattern} element={<TeamDetailRoute />} />
        <Route path={ROUTES.requests} element={<RequestsListPage />} />
        <Route path={ROUTES.kanban} element={<KanbanRoute />} />
        <Route path={ROUTES.newRequest} element={<RequestWizardPage />} />
        <Route path={ROUTES.requestDetailPattern} element={<RequestDetailRoute />} />
        <Route path={ROUTES.users} element={<UsersListRoute />} />
        <Route path={ROUTES.auditLog} element={<AuditLogListPage />} />
        <Route path={ROUTES.analytics} element={<AnalyticsPage />} />
      </Route>

      <Route path={ROUTES.notFound} element={<NotFoundPage />} />
    </Routes>
  );
}
